import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileUp, Upload, Loader, Check, AlertCircle } from 'lucide-react';
import { recipes as recipeApi } from '../utils/api';

const PaprikaImport = () => {
  const navigate = useNavigate();
  const paprikaInputRef = useRef(null);
  
  const [paprikaImporting, setPaprikaImporting] = useState(false);
  const [paprikaResult, setPaprikaResult] = useState(null);
  const [paprikaError, setPaprikaError] = useState(null);

  const handlePaprikaImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.paprikarecipes')) {
      setPaprikaError('Please select a valid .paprikarecipes file');
      return;
    }
    
    setPaprikaImporting(true);
    setPaprikaError(null);
    setPaprikaResult(null);
    
    try {
      const response = await recipeApi.importFromPaprika(file);
      setPaprikaResult(response.data);
    } catch (err) {
      console.error('Paprika import failed:', err);
      setPaprikaError(err.response?.data?.detail || 'Failed to import recipes from Paprika file');
    } finally {
      setPaprikaImporting(false);
      // Reset file input
      if (paprikaInputRef.current) {
        paprikaInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-slate-800">Import from Paprika</h1>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <FileUp size={28} className="text-orange-500" />
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Paprika Recipe Import</h2>
              <p className="text-sm text-gray-500">Bring all your recipes to Honey</p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">How to export from Paprika:</p>
            <ol className="text-sm text-gray-600 space-y-1.5 list-decimal pl-4">
              <li>Open Paprika app → <strong>Settings</strong> (in sidebar)</li>
              <li>Tap <strong>Export Recipes</strong></li>
              <li>Select <strong>"Paprika Recipe Format"</strong></li>
              <li>Turn <strong>OFF</strong> "Use Unicode filenames"</li>
              <li>Tap <strong>Export</strong> (saves to Files/iCloud Drive on your phone)</li>
              <li>Tap the below button to access the file and import to Honey</li>
            </ol>
          </div>
          
          {/* File Input */}
          <input
            ref={paprikaInputRef}
            type="file"
            accept=".paprikarecipes"
            onChange={handlePaprikaImport}
            className="hidden"
            id="paprika-file-input"
            data-testid="paprika-file-input"
          />
          
          {!paprikaResult && (
            <button
              onClick={() => paprikaInputRef.current?.click()}
              disabled={paprikaImporting}
              className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-xl font-medium transition-colors text-lg"
              data-testid="select-paprika-file-btn"
            >
              {paprikaImporting ? (
                <>
                  <Loader size={22} className="animate-spin" />
                  Importing recipes...
                </>
              ) : (
                <>
                  <Upload size={22} />
                  Select .paprikarecipes file
                </>
              )}
            </button>
          )}
          
          {/* Paprika Import Results */}
          {paprikaResult && (
            <div className="p-5 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-center gap-2 text-green-700 font-semibold text-lg mb-3">
                <Check size={24} />
                Imported {paprikaResult.imported_count} recipe{paprikaResult.imported_count !== 1 ? 's' : ''}!
              </div>
              {paprikaResult.imported_recipes?.length > 0 && (
                <ul className="text-sm text-green-600 space-y-1 mb-4">
                  {paprikaResult.imported_recipes.slice(0, 5).map((r, i) => (
                    <li key={i}>• {r.title}</li>
                  ))}
                  {paprikaResult.imported_recipes.length > 5 && (
                    <li className="text-gray-500">...and {paprikaResult.imported_recipes.length - 5} more</li>
                  )}
                </ul>
              )}
              {paprikaResult.error_count > 0 && paprikaResult.errors && (
                <div className="text-sm text-amber-600 mb-3">
                  <p className="font-medium mb-1">{paprikaResult.error_count} recipe{paprikaResult.error_count !== 1 ? 's' : ''} skipped:</p>
                  <ul className="space-y-0.5 text-amber-700">
                    {paprikaResult.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>• {err.recipe}: {err.error}</li>
                    ))}
                    {paprikaResult.errors.length > 5 && (
                      <li className="text-gray-500">...and {paprikaResult.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/recipes')}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  data-testid="view-recipe-box-btn"
                >
                  View Recipe Box
                </button>
                <button
                  onClick={() => {
                    setPaprikaResult(null);
                    setPaprikaError(null);
                  }}
                  className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                  data-testid="import-more-btn"
                >
                  Import More
                </button>
              </div>
            </div>
          )}
          
          {/* Paprika Error */}
          {paprikaError && (
            <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle size={20} />
                <span className="text-sm">{paprikaError}</span>
              </div>
              <button
                onClick={() => setPaprikaError(null)}
                className="mt-2 text-sm text-red-600 hover:underline"
              >
                Try again
              </button>
            </div>
          )}
        </div>
        
        {/* Info note */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Your Paprika recipes will be imported with all ingredients,</p>
          <p>instructions, notes, and nutrition info.</p>
        </div>
      </div>
    </div>
  );
};

export default PaprikaImport;
